import * as THREE from 'three'
const TWEEN = require('@tweenjs/tween.js')
import * as UrlConstants from '@/constants/url-constants'
import positionSettings from '@/settings/position-settings.json'
import References from '@/singletons/references'
import SentrySingleton from '@/singletons/sentry-singleton'

type AnimationCallback = () => void;
const animationSpeed = 1.5

export default class BagAnimationService {
    private cameraTween: any
    private startTween1: any
    private startTween2: any
    private mixer: THREE.AnimationMixer
    private action: THREE.AnimationAction
    private clock: THREE.Clock

    constructor() {
        const animation = References.instance.getBagReferences().gltf.animations[1]
        this.mixer = new THREE.AnimationMixer(References.instance.getBagReferences().bag)
        this.action = this.mixer.clipAction(animation)
        this.action.setLoop(THREE.LoopOnce, 1)
        this.action.clampWhenFinished = true

        this.action.time = this.action.getClip().duration
        this.action.timeScale = animationSpeed
        this.action.play()

        this.clock = new THREE.Clock()
        const delta = this.clock.getDelta()
        this.mixer.update(delta)

    }

    startTweens(allOptionSet: boolean, callback: AnimationCallback) {
        try {
            const urlParams = new URLSearchParams(window.location.search)
            const skipAnim = urlParams.has(UrlConstants.noStartAnimation) && urlParams.get(UrlConstants.noStartAnimation) == 'true'
            if (skipAnim || window.location.pathname.includes('renderer.html')) {
                References.instance.getSceneReferences().camera.position.copy(this.toVector3(positionSettings.cameraEndPos))
                callback()
                return
            }
            this.startTween1 = new TWEEN.Tween(this.toVector3(positionSettings.cameraStartPos))
                .to(this.toVector3(this.determineStartTarget(allOptionSet)), 4000)
                .easing(TWEEN.Easing.Quartic.InOut)
                .onUpdate((object: any) => {
                    References.instance.getSceneReferences().camera.position.copy(object)
                })
                .onComplete(function () {
                    callback()
                })
                .start()

            this.startTween2 = new TWEEN.Tween({ x: 0 })
                .to({ x: -360 }, 4000)
                .easing(TWEEN.Easing.Quartic.InOut)
                .onUpdate((val: any) => {
                    References.instance.getBagReferences().parent.rotation.set(0, THREE.MathUtils.degToRad(val.x), 0)
                })
                .onComplete(function () { })
                .start()

        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
        }
    }

    private determineStartTarget(allOptionSet: boolean) {
        if (allOptionSet) {
            return positionSettings.cameraPositionsFullorder[positionSettings.cameraPositionsFullorder.length - 1].pos
        }
        return positionSettings.cameraEndPos
    }

    tweenCamera(numbers: number[], instant: boolean) {
        try {
            if (this.cameraTween !== undefined && this.cameraTween !== null) {
                this.cameraTween.stop()
            }
            const pos = this.toVector3(numbers)

            if (instant) {
                References.instance.getSceneReferences().camera.position.copy(pos)
            }
            else {
                const camPos = References.instance.getSceneReferences().camera.position.clone()
                if (this.isRoundedTween(camPos, pos)) {
                    this.roundedTween(camPos, pos)
                } else {
                    this.normalTween(camPos, pos)
                }
            }
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
        }
    }

    private roundedTween(camPos, pos) {
        var middlePos = this.getPointInBetween(camPos, pos)
        var sign = camPos.x > 0.001 ? 1 : -1
        if (pos.x < -0.3) {
            sign = -1
        }
        middlePos.x = sign * 1.0

        var tween = new TWEEN.Tween(middlePos)
            .to(pos, 1000)
            .easing(TWEEN.Easing.Sinusoidal.InOut)
            .onUpdate((val: any) => {
                References.instance.getSceneReferences().camera.position.copy(val)
            })

        this.cameraTween = new TWEEN.Tween(camPos)
            .to(middlePos, 1000)
            .easing(TWEEN.Easing.Sinusoidal.InOut)
            .onUpdate((val: any) => {
                References.instance.getSceneReferences().camera.position.copy(val)
            })
            .onComplete(function () { })
            .chain(tween)
            .start()
    }

    private isRoundedTween(camPos, pos) {
        const threshold = 0.5
        return Math.sign(camPos.z) !== Math.sign(pos.z) && (camPos.z > threshold || camPos.z < -threshold)
    }

    private normalTween(camPos, pos) {
        this.cameraTween = new TWEEN.Tween(camPos)
            .to(pos, 1500)
            .easing(TWEEN.Easing.Sinusoidal.InOut)
            .onUpdate((val: any) => {
                References.instance.getSceneReferences().camera.position.copy(val)
            })
            .onComplete(function () { })
            .start()
    }

    private getPointInBetween(pointA, pointB) {
        var percentage = 0.5
        var dir = pointB.clone().sub(pointA)
        var len = dir.length()
        dir = dir.normalize().multiplyScalar(len * percentage)
        return pointA.clone().add(dir)
    }

    private toVector3(numbers: number[]): THREE.Vector3 {
        return new THREE.Vector3(numbers[0], numbers[1], numbers[2])
    }

    update() {
        try {
            const delta = this.clock.getDelta()
            TWEEN.update()
            if (this.mixer) {
                this.mixer.update(delta)
            }
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
        }
    }

    toggleAnimation() {
        try {
            if (this.isBagClosed()) {
                this.openBag()
            } else {
                this.closeBag()
            }
        }
        catch (err) {
            SentrySingleton.instance.captureException(err)
        }
    }

    private openBag() {
        if (this.action === undefined || this.action.time === 0) {
            return
        }
        if (this.action.time === 0) {
            this.action.time = this.action.getClip().duration
        }
        this.action.paused = false
        this.action.setLoop(THREE.LoopOnce, 0)
        this.action.timeScale = -animationSpeed
        this.action.play()
    }

    private openBagInstant() {
        if (this.action === undefined) {
            return
        }
        this.action.time = 0
        this.action.timeScale = 0
        this.action.play()
    }

    private closeBagInstant() {
        if (this.action === undefined) {
            return
        }
        this.action.time = this.action.getClip().duration
        this.action.timeScale = 0
        this.action.play()
    }

    private closeBag() {
        if (this.action === undefined) {
            return
        }
        if (this.action.time === this.action.getClip().duration) {
            return
        }
        if (this.action.time === this.action.getClip().duration) {
            this.action.time = 0
        }
        this.action.paused = false
        this.action.setLoop(THREE.LoopOnce, 1)
        this.action.timeScale = animationSpeed
        this.action.play()
    }

    private isBagClosed() {
        return this.action.time / this.action.getClip().duration >= 1
    }

    setBagOpen(open: boolean, instant: boolean) {
        if (instant) {
            if (open) {
                this.openBagInstant()
            } else {
                this.closeBagInstant()
            }
        }
        else {
            if (open) {
                this.openBag()
            } else {
                this.closeBag()
            }
        }

    }

    cancelAll() {
        if (this.startTween1) {
            this.startTween1.stop()
        }

        if (this.startTween2) {
            this.startTween2.stop()
        }
        if (this.cameraTween) {
            this.cameraTween.stop()
        }
    }
}